"use client";

import type { CSSProperties, RefObject } from "react";
import { X } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useDismissibleHomeBanner } from "./hooks/useDismissibleHomeBanner";
import type { StayRule } from "@/app/api/utils/getHighestDiscount";

const STAY_DISCOUNT_BANNER_COOKIE = "stay_discount_banner_dismissed";

const PLANE_PATH =
  "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z";

// Punches the two half-circle notches that make the shape read as a ticket.
const ticketNotchMask = (notchAt: string): CSSProperties => ({
  mask: `radial-gradient(5px at ${notchAt} 0, #0000 96%, #000) center top / 100% 51% no-repeat, radial-gradient(5px at ${notchAt} 100%, #0000 96%, #000) center bottom / 100% 51% no-repeat`,
});

const backTicketStyle: CSSProperties = {
  margin: "-26px 0 0 -20px",
  transform: "rotate(12deg)",
  filter: "drop-shadow(rgba(76, 60, 180, 0.18) 0 4px 6px)",
};

const frontTicketStyle: CSSProperties = {
  margin: "-20px 0 0 -40px",
  transform: "rotate(-9deg)",
  filter:
    "drop-shadow(rgba(66, 120, 255, 0.28) 0 8px 10px) drop-shadow(rgba(11, 17, 32, 0.12) 0 2px 2px)",
};

const backgroundClasses =
  "relative flex min-h-20 items-center overflow-hidden [background:linear-gradient(114deg,rgba(255,255,255,0.00)_46.91%,#D1F4FD_69.9%),#FFF] dark:[background:linear-gradient(114deg,rgba(255,255,255,0.00)_46.91%,rgba(66,230,255,0.12)_69.9%),#0B1120] dark:border-b dark:border-[rgba(66,230,255,0.25)]";

const DiscountTicket = ({ percent, off }: { percent: number; off: string }) => (
  <div className="relative h-20 w-[88px] shrink-0" aria-hidden="true">
    <div
      className="absolute left-1/2 top-1/2 h-10 w-[60px]"
      style={backTicketStyle}
    >
      <div
        className="h-full w-full rounded-lg bg-[linear-gradient(135deg,#F0FCFF_0%,#CFC7FB_100%)]"
        style={ticketNotchMask("30%")}
      />
    </div>
    <div
      className="absolute left-1/2 top-1/2 h-11 w-[70px]"
      style={frontTicketStyle}
    >
      <div
        className="relative h-full w-full overflow-hidden rounded-[9px] bg-[linear-gradient(135deg,#5EEBFF_0%,#6FA8FB_45%,#8B5EF7_100%)]"
        style={ticketNotchMask("32%")}
      >
        <div className="absolute inset-x-0 top-0 h-1/2 bg-[linear-gradient(rgba(255,255,255,0.5),rgba(255,255,255,0))]" />
        <div className="absolute inset-0 rounded-[9px] shadow-[inset_0_-3px_6px_rgba(60,20,160,0.25),inset_0_1px_1px_rgba(255,255,255,0.7)]" />
        <div className="absolute inset-y-0 left-0 flex w-[32%] items-center justify-center">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="#FFFFFF"
            className="rotate-45"
          >
            <path d={PLANE_PATH} />
          </svg>
        </div>
        <div className="absolute inset-y-[7px] left-[32%] border-l-[1.5px] border-dashed border-white/75" />
        <div className="absolute inset-y-0 left-[32%] right-0 flex flex-col items-center justify-center font-inter text-white [text-shadow:rgba(40,20,120,0.35)_0_1px_2px]">
          <span className="text-base font-extrabold leading-4 tracking-[-0.5px]">
            {percent}%
          </span>
          <span className="text-[7px] font-bold leading-[9px] tracking-[0.12em]">
            {off}
          </span>
        </div>
      </div>
    </div>
  </div>
);

type Props = {
  stayRule: Pick<StayRule, "percentage" | "minCheckIn">;
  searchRef?: RefObject<HTMLDivElement>;
};

const StayDiscountBanner = ({ stayRule, searchRef }: Props) => {
  const locale = useLocale();
  const t = useTranslations("StayDiscountBanner");
  const { isVisible, dismiss } = useDismissibleHomeBanner(
    STAY_DISCOUNT_BANNER_COOKIE
  );

  if (!isVisible) {
    return null;
  }

  const handleScrollToSearch = () => {
    searchRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const { percentage } = stayRule;
  // min_check_in is a plain date, so format it in UTC to keep the same day
  const checkInFrom = stayRule.minCheckIn
    ? new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "long",
        timeZone: "UTC",
      }).format(new Date(`${stayRule.minCheckIn}T00:00:00Z`))
    : "";
  const eyebrow = t("eyebrow", { percent: percentage });
  const headline = t("headline", { date: checkInFrom });
  const ticket = <DiscountTicket percent={percentage} off={t("off")} />;
  const closeButton = (className: string, size: number) => (
    <button
      type="button"
      onClick={dismiss}
      aria-label={t("closeBanner")}
      className={`absolute cursor-pointer px-0 text-sleap-gray-650 dark:text-lightwhite ${className}`}
    >
      <X size={size} />
    </button>
  );

  return (
    <>
      <div className={`${backgroundClasses} lg:hidden`}>
        <svg
          width="160"
          height="80"
          viewBox="0 0 160 80"
          fill="none"
          className="pointer-events-none absolute right-1.5 top-0"
          aria-hidden="true"
        >
          <path
            d="M8 72 C 60 72, 80 30, 120 34 S 150 40, 152 16"
            stroke="#0E7490"
            strokeOpacity="0.25"
            strokeWidth="1.2"
            strokeDasharray="2.5 4"
            strokeLinecap="round"
          />
        </svg>
        <button
          type="button"
          onClick={handleScrollToSearch}
          className="relative flex w-full cursor-pointer items-center gap-2 py-0 pe-8 ps-1 text-start"
        >
          {ticket}
          <span className="flex min-w-0 flex-col gap-1 py-2 font-inter text-xs font-semibold leading-4">
            <span className="text-[#0E7490] dark:text-[#42E6FF]">
              {eyebrow}
            </span>
            <span className="text-sleap-gray-900 dark:text-lightwhite">
              {headline}
            </span>
            <span className="text-[10px] leading-[14px] text-[#6D28D9] underline underline-offset-2 dark:text-[#B69CFF]">
              {t("cta")} →
            </span>
          </span>
        </button>
        {closeButton("right-3 top-3", 12)}
      </div>

      <div
        className={`${backgroundClasses} hidden justify-center px-14 lg:flex`}
      >
        <svg
          width="520"
          height="80"
          viewBox="0 0 520 80"
          fill="none"
          className="pointer-events-none absolute right-10 top-0"
          aria-hidden="true"
        >
          <path
            d="M10 70 C 140 70, 200 10, 330 22 S 470 50, 505 14"
            stroke="#0E7490"
            strokeOpacity="0.28"
            strokeWidth="1.5"
            strokeDasharray="3 5"
            strokeLinecap="round"
          />
          <circle cx="10" cy="70" r="3.5" fill="#0E7490" fillOpacity="0.35" />
          <g transform="translate(505 14) rotate(35)">
            <path
              transform="translate(-9 -9) scale(0.75)"
              d={PLANE_PATH}
              fill="#0E7490"
              fillOpacity="0.45"
            />
          </g>
        </svg>
        <button
          type="button"
          onClick={handleScrollToSearch}
          className="relative flex w-full max-w-[1200px] cursor-pointer items-center justify-center gap-10 text-start xl:gap-20 font-inter"
        >
          <span className="flex items-center gap-4">
            {ticket}
            <span className="flex max-w-[430px] flex-col gap-0.5 text-lg font-semibold leading-6">
              <span className="text-[#0E7490] dark:text-[#42E6FF]">
                {eyebrow}
              </span>
              <span className="text-sleap-gray-900 dark:text-lightwhite">
                {headline}
              </span>
            </span>
          </span>
          <span className="flex max-w-[330px] flex-col gap-1">
            <span className="text-sm leading-5 text-[#374151] dark:text-lightwhite">
              {t("description")}
            </span>
            <span className="text-xs leading-4 text-sleap-gray-650 dark:text-sleap-gray-100">
              {t("deadlineNote")}
            </span>
          </span>
          <span className="shrink-0 rounded-md bg-[linear-gradient(135deg,#0A7C93_0%,#7A45E8_100%)] px-4 py-2 text-xs font-semibold leading-4 text-white transition-opacity hover:opacity-90">
            {t("cta")}
          </span>
        </button>
        {closeButton("right-4 top-4", 16)}
      </div>
    </>
  );
};

export default StayDiscountBanner;
